/**
 * Zod schemas for ward management (V2-E1, HAN-83). The single source of truth
 * for the *shape and rules* of a ward create/edit payload, shared so the
 * super-admin web form and the API validate identically — the API's
 * class-validator DTOs mirror these rules field-for-field, and the constants
 * below are imported by both sides so they can't drift.
 *
 * Kept dependency-light: only `zod`. No imports from the API or drizzle.
 */
import { z } from "zod";

/** Max length of a ward's display name (e.g. "Oncology"). */
export const WARD_NAME_MAX_LENGTH = 120;

/**
 * A ward's short code is *exactly three characters* and forms the prefix of
 * every handover code (`ONC-482`). The validate-code parser slices the first
 * three characters off the entered code to find the ward (see the API's
 * `parseCodeFromInput`), so anything other than three would silently break
 * code validation. Stored uppercased; the prefix alphabet is unrestricted
 * (only the random suffix avoids ambiguous glyphs — HAN-40), so any
 * alphanumeric triple is allowed.
 */
export const WARD_SHORT_CODE_LENGTH = 3;
export const WARD_SHORT_CODE_PATTERN = /^[A-Z0-9]{3}$/;

const wardName = z.string().trim().min(1, "Ward name is required").max(WARD_NAME_MAX_LENGTH);

const wardShortCode = z
  .string()
  .trim()
  .toUpperCase()
  .regex(WARD_SHORT_CODE_PATTERN, "Short code must be exactly 3 letters or digits");

/** Payload for creating a ward — both fields required. */
export const wardCreateSchema = z.object({
  name: wardName,
  shortCode: wardShortCode,
});

export type WardCreateInput = z.infer<typeof wardCreateSchema>;

/**
 * Payload for editing a ward. Every field is optional (a PATCH), but at least
 * one must be present — an empty edit is rejected so the audit log never
 * records a no-op "Ward edited".
 */
export const wardUpdateSchema = z
  .object({
    name: wardName.optional(),
    shortCode: wardShortCode.optional(),
  })
  .refine((value) => value.name !== undefined || value.shortCode !== undefined, {
    message: "Provide at least one field to update",
  });

export type WardUpdateInput = z.infer<typeof wardUpdateSchema>;
